"use client";

import { Fragment } from "react";
import { useTranslation } from "@/lib/i18n/context";
import { TRAIL_COPY, type TrailLang, type ChangeKind } from "@/lib/trail/data";

/** Inline-markdown renderer: `**bold**`, `*italic*`, `` `code` ``. */
function renderText(s: string) {
  const re = /(\*\*[^*]+\*\*|`[^`]+`|\*[^*]+\*)/g;
  return s.split(re).map((p, i) => {
    if (p.startsWith("**") && p.endsWith("**")) {
      return (
        <strong key={i} className="font-medium text-[var(--ink)]">
          {p.slice(2, -2)}
        </strong>
      );
    }
    if (p.startsWith("`") && p.endsWith("`")) {
      return (
        <code key={i} className="rounded bg-[var(--paper-2)] px-1.5 py-0.5 font-mono text-[0.85em] text-[var(--ink)]">
          {p.slice(1, -1)}
        </code>
      );
    }
    if (p.startsWith("*") && p.endsWith("*") && p.length > 2) {
      return (
        <em key={i} className="font-display italic text-[var(--ink)]">
          {p.slice(1, -1)}
        </em>
      );
    }
    return <Fragment key={i}>{p}</Fragment>;
  });
}

/** Tag pill colours per change kind — base hue holds in dark, alpha carries it. */
const KIND_CLASS: Record<ChangeKind, string> = {
  new: "border-[var(--terracotta)]/40 bg-[var(--terracotta)]/10 text-[var(--terracotta)]",
  improved: "border-[var(--moss)]/40 bg-[var(--moss)]/10 text-[var(--moss)] dark:text-[var(--moss)]",
  fixed: "border-[var(--rule)] bg-[var(--paper-2)]/60 text-[var(--ink-soft)]",
};

export function OurTrailContent() {
  const { lang } = useTranslation();
  const langKey: TrailLang = lang === "en" ? "en" : "sl";
  const cp = TRAIL_COPY[langKey];

  return (
    <main className="mx-auto max-w-[1280px] px-6 pb-32 pt-12 text-[var(--ink)]">
      {/* Header — mirrors the in-app /help + /faq template. */}
      <header className="mb-12 border-b border-[var(--rule)]/30 pb-6">
        <p className="mb-2.5 flex items-center gap-2.5 font-mono text-[10px] uppercase tracking-[0.18em] text-[var(--ink-soft)]">
          <span className="h-1.5 w-1.5 rounded-full bg-[var(--terracotta)]" />
          {cp.eyebrow}
        </p>
        <h1 className="font-display text-[clamp(40px,5vw,64px)] leading-[0.95] tracking-[-0.025em] text-balance">
          {cp.title.a}
          <em className="font-medium italic text-[var(--terracotta)]">{cp.title.em}</em>.
        </h1>
        <p className="mt-3 max-w-[720px] font-display text-[17px] italic leading-[1.5] text-[var(--ink-soft)]">
          {cp.subtitle}
        </p>
      </header>

      {/* Timeline — a single hairline rail with a node per release. */}
      <div className="mx-auto max-w-[820px]">
        <ol className="relative space-y-14 before:absolute before:bottom-2 before:left-[7px] before:top-2 before:w-px before:bg-[var(--rule)] md:before:left-[calc(120px+7px)]">
          {cp.entries.map((entry, idx) => (
            <li key={entry.version} className="relative md:grid md:grid-cols-[120px_1fr] md:gap-8">
              {/* Date column (left on md+). */}
              <div className="mb-3 pl-7 md:mb-0 md:pl-0 md:pr-4 md:text-right">
                <time
                  dateTime={entry.date}
                  className="block font-mono text-[11px] uppercase tracking-[0.14em] text-[var(--ink-soft)]"
                >
                  {entry.dateLabel}
                </time>
                {idx === 0 && (
                  <span className="mt-1.5 inline-block rounded-full border border-[var(--terracotta)]/40 bg-[var(--terracotta)]/10 px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.14em] text-[var(--terracotta)]">
                    {cp.latestLabel}
                  </span>
                )}
              </div>

              {/* Node on the rail. */}
              <span
                aria-hidden
                className={`absolute left-0 top-1 h-3.5 w-3.5 rounded-full border-2 md:left-[120px] ${
                  idx === 0
                    ? "border-[var(--terracotta)] bg-[var(--terracotta)]"
                    : "border-[var(--rule)] bg-[var(--paper)]"
                }`}
              />

              {/* Entry body. */}
              <div className="pl-7 md:pl-6">
                <h2 className="flex flex-wrap items-baseline gap-x-3 font-display text-[clamp(22px,2.6vw,28px)] leading-tight tracking-[-0.02em] text-[var(--ink)]">
                  {entry.title}
                  <span className="font-mono text-[12px] font-normal not-italic tracking-[0.1em] text-[var(--ink-soft)]">
                    v{entry.version}
                  </span>
                </h2>
                {entry.intro && (
                  <p className="mt-2 max-w-[620px] font-display text-[16px] italic leading-[1.6] text-[var(--ink-soft)]">
                    {entry.intro}
                  </p>
                )}
                <ul className="mt-5 space-y-3.5">
                  {entry.changes.map((c, i) => (
                    <li key={i} className="flex items-start gap-3">
                      <span
                        className={`mt-0.5 shrink-0 self-start rounded-full border px-2 py-0.5 font-mono text-[9px] uppercase tracking-[0.12em] ${KIND_CLASS[c.kind]}`}
                      >
                        {cp.kindLabels[c.kind]}
                      </span>
                      <p className="m-0 max-w-[600px] font-display text-[15.5px] leading-[1.6] text-[var(--ink-soft)]">
                        {renderText(c.text)}
                      </p>
                    </li>
                  ))}
                </ul>
              </div>
            </li>
          ))}
        </ol>
      </div>
    </main>
  );
}
